import { FormEvent, KeyboardEvent, useRef, useState } from "react";
import { CreateAnnotationPayload } from "../../types";

interface Props {
  traceId: string;
  isSubmitting: boolean;
  onSubmit: (payload: CreateAnnotationPayload) => void;
}

const SEVERITY_OPTIONS = ["blocker", "high", "medium", "low", "none"];

function normalizeSeverity(value?: string): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (SEVERITY_OPTIONS.includes(normalized)) {
    return normalized;
  }
  switch (normalized) {
    case "critical":
    case "major":
      return "blocker";
    case "minor":
      return "low";
    case "info":
    case "informational":
    case "none":
      return "none";
    default:
      return undefined;
  }
}

function AnnotationForm({ traceId, isSubmitting, onSubmit }: Props) {
  const formRef = useRef<HTMLFormElement | null>(null);
  const [severity, setSeverity] = useState<string>("high");
  const [failureMode, setFailureMode] = useState<string>("");
  const [note, setNote] = useState<string>("");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!failureMode && !note) return;
    onSubmit({
      traceId,
      severity,
      failureMode: failureMode || undefined,
      note: note || undefined,
      status: "needs_attention"
    });
    setNote("");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  return (
    <form ref={formRef} className="annotation-form" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      <div className="annotation-form__row">
        <label>
          <span>Severity</span>
          <select value={severity} onChange={(event) => setSeverity(event.target.value)}>
            {SEVERITY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Failure mode</span>
          <input
            type="text"
            placeholder="e.g. wrong recipient tone"
            value={failureMode}
            onChange={(event) => setFailureMode(event.target.value)}
          />
        </label>
      </div>
      <label>
        <span>Notes</span>
        <p className="annotation-form__helper">Record what's wrong, not solutions.</p>
        <textarea
          placeholder="Describe the failure mode or good vibes."
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </label>
      <div className="annotation-form__actions">
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving…" : "Add annotation"}
        </button>
      </div>
    </form>
  );
}
export default AnnotationForm;
